import React, { useEffect, useState } from "react";
import {
  TextInput,
  SimpleForm,
  required,
  SelectInput,
  ReferenceInput,
  RadioButtonGroupInput,
  Toolbar,
  FormDataConsumer,
  useRedirect,
  useRefresh,
  useDataProvider,
  CreateProps,
  ToolbarProps,
  BooleanInput,
} from "react-admin";
import SanitizedGrid from "components/SanitizedGrid";
import { FormControl, Grid, Typography } from "@material-ui/core";
import CustomImageInput from "components/CustomImageInput";
import LevelInput from "components/LevelInput";
import LevelGroupInput from "components/LevelGroupInput";
import {
  AcademicYearResources,
  CcaAcademicYearResources,
  GetParam,
} from "utils/tools";
import moment from "moment";
import SchoolInput from "components/SchoolInput";
import CustomDatePickerInput from "components/CustomDatePickerInput";
import UniqueCodeInput from "./components/UniqueCodeInput";
import SaveButton from "./components/SaveButton";
import { isCalculationCostCreateStudentInvalid } from "utils/validation";
import NameInput from "../../components/NameInput";
import DropOffAuthInput from "./components/DropOffAuthInput";
import { genderList, scheduleTypeList } from "./const";
import CustomNumberInput from "../../components/CustomNumberInput";
import { StudentCreateCcaFixedLocation } from "./components/StudentCreateCcaFixedLocation";
import { StudentCreateCcaDoorToDoor } from "./components/StudentCreateCcaDoorToDoor";
import { StudentCreateRegular } from "./components/StudentCreateRegular";
import { StudentCreateCostCalculation } from "./components/StudentCreateCostCalculation";
import CreateWrapper from "../../components/CreateWrapper";
import StudentSubsidyInput from "./components/StudentSubsidyInput";

const StudentCreate = (props: CreateProps) => {
  const param = GetParam();
  const [academicTerms, setAcademicTerms] = useState<IAcademicTerm[]>([]);
  const [ccaAcademicTerms, setCcaAcademicTerms] = useState<ICcaAcademicTerm[]>(
    [],
  );
  const [ccaFixedLocationDropOffs, setCcaFixedLocationDropOffs] = useState<
    ICcaFixedLocationDropOff[]
  >([]);
  const [ccaWaves, setCcaWaves] = useState<ICcaWave[]>([]);
  const [holidays, setHolidays] = useState<IHoliday[]>([]);
  const [ccaHolidays, setCcaHolidays] = useState<IHoliday[]>([]);
  const [resultCalculations, setResultCalculations] =
    useState<IRegularAndCcaCalculationResults | null>(null);
  const [idAcademicYear, setIdAcademicYear] = useState<number>();
  const [idCcaAcademicYear, setIdCcaAcademicYear] = useState<number>();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isCalculationButtonClicked, setIsCalculationButtonClicked] =
    useState(false);

  const [schools, setSchools] = React.useState<ISchool[]>([]);
  const [schoolId, setSchoolId] = React.useState<number | null>(null);
  const [selectedSchool, setSelectedSchool] = React.useState<ISchool | null>(
    null,
  );
  const [isNotTakeSchedule, setIsNotTakeSchedule] = useState(false);
  const [dropOffAddressType, setDropOffAddressType] = useState<string>();

  const redirect = useRedirect();
  const refresh = useRefresh();
  const provider = useDataProvider();

  const format = "DD/MM/YY";

  const breadcrumb = [
    {
      title: "Parent",
      link: `/parents/${param.get("parentId")}/show`,
    },
    {
      title: "Students",
      link: `/parents/${param.get("parentId")}/show/2`,
    },
    { title: "Create", link: "" },
  ];

  const CustomToolbar = (props: ToolbarProps) => {
    return (
      <Toolbar {...props}>
        <FormDataConsumer>
          {() => (
            <div>
              <SaveButton
                invalid={props.invalid}
                isCalculationButtonClicked={isCalculationButtonClicked}
              />
            </div>
          )}
        </FormDataConsumer>
      </Toolbar>
    );
  };

  const CustomRedirect = () => {
    redirect(`/parents/${param.get("parentId")}/show/2`);
    refresh();
  };

  const academicYearResources = AcademicYearResources(idAcademicYear);
  const ccaAcademicYearResources = CcaAcademicYearResources(idCcaAcademicYear);

  useEffect(() => {
    provider
      .getList("academicTerms", {
        filter: { AcademicYearId: Number(idAcademicYear) },
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "Id",
          order: "ASC",
        },
      })
      .then(({ data }) => {
        setAcademicTerms(data as IAcademicTerm[]);
      });

    provider
      .getList("holidays", {
        filter: { AcademicYearId: Number(idAcademicYear) },
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "Id",
          order: "ASC",
        },
      })
      .then(({ data }) => {
        setHolidays(data as IHoliday[]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idAcademicYear]);

  useEffect(() => {
    provider
      .getList("ccaAcademicTerms", {
        filter: { CcaAcademicYearId: Number(idCcaAcademicYear) },
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "Id",
          order: "ASC",
        },
      })
      .then(({ data }) => {
        setCcaAcademicTerms(data as ICcaAcademicTerm[]);
      });

    provider
      .getList("ccaHolidays", {
        filter: { CcaAcademicYearId: Number(idCcaAcademicYear) },
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "Id",
          order: "ASC",
        },
      })
      .then(({ data }) => {
        setCcaHolidays(data as IHoliday[]);
      });

    if (selectedSchool) {
      provider
        .getList("ccaFixedLocationDropOffs", {
          filter: { schoolId: Number(selectedSchool.id), Status: "active" },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then(({ data }) => {
          setCcaFixedLocationDropOffs(data as ICcaFixedLocationDropOff[]);
        });

      provider
        .getList("busSchedules", {
          filter: { schoolId: Number(selectedSchool.id), type: "cca" },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then(({ data }) => {
          setCcaWaves(data as ICcaWave[]);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCcaAcademicYear, selectedSchool]);

  // Regular
  let includeDates: IIncludeDates[] = [];
  academicTerms.forEach((academicTerm: IAcademicTerm) => {
    let temp: IIncludeDates = {
      startDate: academicTerm.startDate,
      endDate: academicTerm.endDate,
    };

    includeDates.push(temp);
  });
  let excludeDates: IExcludeDates[] = [];
  holidays.forEach((holiday: IHoliday) => {
    let temp: IExcludeDates = {
      startDate: holiday.date,
    };

    excludeDates.push(temp);
  });

  // CCA
  let ccaIncludeDates: IIncludeDates[] = [];
  ccaAcademicTerms.forEach((ccaAcademicTerm: ICcaAcademicTerm) => {
    let temp: IIncludeDates = {
      startDate: ccaAcademicTerm.startDate,
      endDate: ccaAcademicTerm.endDate,
    };

    ccaIncludeDates.push(temp);
  });
  let ccaExcludeDates: IExcludeDates[] = [];
  ccaHolidays.forEach((ccaHoliday: IHoliday) => {
    let temp: IExcludeDates = {
      startDate: ccaHoliday.date,
    };

    ccaExcludeDates.push(temp);
  });

  useEffect(() => {
    //
    provider
      .getList("schools", {
        filter: {},
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "Id",
          order: "ASC",
        },
      })
      .then((result: any) => {
        console.log(result.data);
        setSchools(result.data);
      })
      .catch(() => {
        setSchools([]);
      });
  }, []);

  useEffect(() => {
    const school = schools.find((school: ISchool) => school.id === schoolId);

    setSelectedSchool(school);

    if (school && school?.ccaDropOffType !== "both") {
      setDropOffAddressType(school?.ccaDropOffType);
    } else {
      setDropOffAddressType("door_to_door");
    }
  }, [schoolId]);

  return (
    <>
      <CreateWrapper
        breadcrumb={breadcrumb}
        headerTitle="New Student From Parent"
        onSuccess={CustomRedirect}
        {...props}
      >
        <SimpleForm toolbar={<CustomToolbar />}>
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">Parent</Typography>
            </Grid>

            <Grid item xs={3}>
              <ReferenceInput
                label="Name"
                source="parentId"
                reference="parents"
                disabled
              >
                <SelectInput
                  optionText={(choice: any) =>
                    `${choice.firstName} ${choice.lastName}`
                  }
                />
              </ReferenceInput>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Student Details</Typography>
            </Grid>

            <Grid item xs={5}>
              <CustomImageInput
                label="Avatar"
                source="photoUrl"
                fieldType="image"
                accept="image/*"
                placeholder="Drop a images (*.jpg/jpeg/png) to upload, or click to select it."
              />
            </Grid>
            <Grid item xs={7}></Grid>

            <Grid item xs={4}>
              <RadioButtonGroupInput
                source="gender"
                choices={genderList}
                validate={required()}
              />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <UniqueCodeInput />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <NameInput label="First Name" source="firstName" />
            </Grid>

            <Grid item xs={4}>
              <NameInput label="Last Name" source="lastName" />
            </Grid>
            <Grid item xs={4}></Grid>

            <Grid item xs={4}>
              <CustomDatePickerInput
                name="dateOfBirth"
                label="Date of Birth *"
                max={moment().format("Y-MM-DD")}
              />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <TextInput label="Medical Condition" source="medicalCondition" />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <FormDataConsumer>
                {({ formData }) => {
                  if (formData.schoolId === 6) {
                    return (
                      <CustomNumberInput
                        source="externalId"
                        label="External id"
                      />
                    );
                  }

                  return <TextInput label="External id" source="externalId" />;
                }}
              </FormDataConsumer>
            </Grid>
            <Grid item xs={8}></Grid>

            <FormDataConsumer>
              {({ formData }) => {
                setSchoolId(formData.schoolId);
                formData.dropOffAddressType = dropOffAddressType;
                return <></>;
              }}
            </FormDataConsumer>

            <Grid item xs={3}>
              <TextInput label="MAC Address" source="macAddress" />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">School Details</Typography>
            </Grid>

            <Grid item xs={4}>
              <SchoolInput
                parentCallback={(value: number) => {
                  setIdAcademicYear(value);
                  setIdCcaAcademicYear(value);
                }}
              />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <LevelInput />
            </Grid>

            <Grid item xs={4}>
              <LevelGroupInput />
            </Grid>
            <Grid item xs={4}></Grid>

            <Grid item xs={4}>
              <DropOffAuthInput />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Schedule</Typography>
            </Grid>

            <Grid item xs={4}>
              <BooleanInput
                source="isNotTakeSchedule"
                label="Create Without Schedule"
                defaultValue={false}
                onChange={(e: boolean) => {
                  setIsNotTakeSchedule(e);
                  setIsLoaded(false);
                }}
              />
            </Grid>

            {isNotTakeSchedule === false && (
              <Grid item={true} xs={12}>
                <FormControl component="fieldset">
                  <RadioButtonGroupInput
                    source="scheduleType"
                    choices={scheduleTypeList}
                    validate={required()}
                  />
                </FormControl>
              </Grid>
            )}

            <FormDataConsumer>
              {({ formData }) =>
                isNotTakeSchedule === false &&
                formData.scheduleType === "regular" && (
                  <StudentCreateRegular
                    setIdAcademicYear={setIdAcademicYear}
                    academicYearResources={academicYearResources}
                    includeDates={includeDates}
                    excludeDates={excludeDates}
                  />
                )
              }
            </FormDataConsumer>

            <FormDataConsumer>
              {({ formData }) =>
                isNotTakeSchedule === false &&
                formData.scheduleType === "cca" && (
                  <Grid
                    xs={12}
                    style={{
                      backgroundColor: "#fffffc",
                      padding: "24px 34px",
                      border: "1px solid #e8e2a7",
                      borderRadius: 12,
                    }}
                  >
                    <Grid item xs={12} style={{ marginBottom: "10px" }}>
                      <Typography variant="h6">CCA Schedule</Typography>
                    </Grid>

                    {/* Show Drop-off Address Type according to the school settings */}
                    <Grid item xs={12}>
                      <RadioButtonGroupInput
                        label="Drop-off Address Type"
                        source="dropOffAddressType"
                        choices={[
                          { id: "fixed_location", name: "Fixed Location" },
                          {
                            id: "door_to_door",
                            name: "Door to Door Location",
                          },
                        ]}
                        disabled={selectedSchool?.ccaDropOffType !== "both"}
                        validate={required()}
                        onChange={(e) => {
                          setDropOffAddressType(e as unknown as string);
                        }}
                      />
                    </Grid>

                    {/* CCA: Fixed Location */}
                    {dropOffAddressType === "fixed_location" && (
                      <>
                        <StudentCreateCcaFixedLocation
                          setIdCcaAcademicYear={setIdCcaAcademicYear}
                          ccaAcademicYearResources={ccaAcademicYearResources}
                          ccaIncludeDates={ccaIncludeDates}
                          ccaExcludeDates={ccaExcludeDates}
                          ccaFixedLocationDropOffs={ccaFixedLocationDropOffs}
                          ccaWaves={ccaWaves}
                          selectedSchool={selectedSchool}
                        />
                      </>
                    )}

                    {/* CCA: Door to Door */}
                    {dropOffAddressType === "door_to_door" && (
                      <>
                        <StudentCreateCcaDoorToDoor
                          setIdCcaAcademicYear={setIdCcaAcademicYear}
                          ccaAcademicYearResources={ccaAcademicYearResources}
                          ccaIncludeDates={ccaIncludeDates}
                          ccaExcludeDates={ccaExcludeDates}
                        />
                      </>
                    )}
                  </Grid>
                )
              }
            </FormDataConsumer>

            <FormDataConsumer>
              {({ formData }) =>
                isNotTakeSchedule === false &&
                formData.scheduleType === "both" && (
                  <>
                    <StudentCreateRegular
                      setIdAcademicYear={setIdAcademicYear}
                      academicYearResources={academicYearResources}
                      includeDates={includeDates}
                      excludeDates={excludeDates}
                    />

                    <Grid
                      xs={12}
                      style={{
                        backgroundColor: "#fffffc",
                        padding: "24px 34px",
                        border: "1px solid #e8e2a7",
                        borderRadius: 12,
                        marginTop: "10px",
                      }}
                    >
                      <Grid item xs={12} style={{ marginBottom: "10px" }}>
                        <Typography variant="h6">CCA Schedule</Typography>
                      </Grid>

                      {/* Show Drop-off Address Type according to the school settings */}
                      <Grid item xs={12}>
                        <RadioButtonGroupInput
                          label="Drop-off Address Type"
                          source="dropOffAddressType"
                          choices={[
                            { id: "fixed_location", name: "Fixed Location" },
                            {
                              id: "door_to_door",
                              name: "Door to Door Location",
                            },
                          ]}
                          disabled={selectedSchool?.ccaDropOffType !== "both"}
                          validate={required()}
                          onChange={(e) => {
                            setDropOffAddressType(e as unknown as string);
                          }}
                        />
                      </Grid>

                      {/* Both: Fixed Location */}
                      {dropOffAddressType === "fixed_location" && (
                        <>
                          <StudentCreateCcaFixedLocation
                            setIdCcaAcademicYear={setIdCcaAcademicYear}
                            ccaAcademicYearResources={ccaAcademicYearResources}
                            ccaIncludeDates={ccaIncludeDates}
                            ccaExcludeDates={ccaExcludeDates}
                            ccaFixedLocationDropOffs={ccaFixedLocationDropOffs}
                            ccaWaves={ccaWaves}
                            selectedSchool={selectedSchool}
                          />
                        </>
                      )}

                      {/* Both: Fixed Location */}
                      {dropOffAddressType === "door_to_door" && (
                        <>
                          <StudentCreateCcaDoorToDoor
                            setIdCcaAcademicYear={setIdCcaAcademicYear}
                            ccaAcademicYearResources={ccaAcademicYearResources}
                            ccaIncludeDates={ccaIncludeDates}
                            ccaExcludeDates={ccaExcludeDates}
                          />
                        </>
                      )}
                    </Grid>
                  </>
                )
              }
            </FormDataConsumer>

            <StudentSubsidyInput selectedSchool={selectedSchool} />

            <StudentCreateCostCalculation
              isLoaded={isLoaded}
              setIsLoaded={setIsLoaded}
              resultCalculations={resultCalculations}
              format={format}
              isCalculationCostCreateStudentInvalid={
                isCalculationCostCreateStudentInvalid
              }
              setResultCalculations={setResultCalculations}
              setIsCalculationButtonClicked={setIsCalculationButtonClicked}
              selectedSchool={selectedSchool}
            />
          </SanitizedGrid>
        </SimpleForm>
      </CreateWrapper>
    </>
  );
};

export default StudentCreate;
